import { c as _c } from "react/compiler-runtime";
import { ShieldCheck } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { CardActions, createCardActionsProps } from "@/src/components/cards/card-actions";
import { EntityDropdownMenu, Field, FieldLayout, hasConflicts, renderBadge } from "@/src/components/cards/common";
import CoverageCardEditable from "@/src/components/cards/coverage-card/coverage-card-editable";
import { ContextMenu, ContextMenuTrigger } from "@/src/components/shadcn/ui/context-menu";
import { ValerieCard, type ValerieCardHeaderVariant } from "@/src/components/valerie-card";
import { useUser } from "@/src/context/user-context";
import { canWriteSOR } from "@/src/lib/rbac";
import { cn } from "@/src/lib/shadcn-utils";
import { Coverage, CoverageType } from "@/src/models/coverage";
const COVERAGE_FIELDS: Field[] = [{
  key: "payer",
  label: "Payer",
  comparable: true,
  section: "Plan"
}, {
  key: "plan",
  label: "Plan Name",
  comparable: true,
  section: "Plan"
}, {
  key: "lineOfBusiness",
  label: "Line of Business",
  comparable: true,
  section: "Plan"
}, {
  key: "effectiveDate",
  label: "Effective Date",
  type: "date",
  comparable: true,
  section: "Plan"
}, {
  key: "expirationDate",
  label: "Expiration Date",
  type: "date",
  comparable: false,
  section: "Plan"
}, {
  key: "subscriberId",
  label: "Subscriber ID",
  comparable: true,
  section: "Subscriber"
}, {
  key: "groupNumber",
  label: "Group Number",
  comparable: true,
  section: "Subscriber"
}, {
  key: "subscriberGivenName",
  label: "Subscriber Given Name",
  comparable: true,
  section: "Subscriber"
}, {
  key: "subscriberFamilyName",
  label: "Subscriber Family Name",
  comparable: true,
  section: "Subscriber"
}, {
  key: "subscriberDateOfBirth",
  label: "Subscriber DOB",
  type: "date",
  comparable: true,
  section: "Subscriber"
}, {
  key: "relationshipToSubscriber",
  label: "Relationship to Subscriber",
  comparable: true,
  section: "Subscriber"
}, {
  key: "phoneNumber",
  label: "Phone Number",
  type: "tel",
  comparable: false,
  section: "Contact"
}, {
  key: "faxPhoneNumber",
  label: "Fax Phone Number",
  type: "tel",
  comparable: false,
  section: "Contact"
}];
export default function CoverageCard(t0) {
  const $ = _c(82);
  const {
    coverage,
    type,
    isEditable: t1,
    compareMode: t2,
    secondaryCoverage: t3,
    eligibilityStatus: t4,
    headerActions,
    className,
    variant: t5,
    onCoverageSaved
  } = t0;
  const isEditable = t1 === undefined ? false : t1;
  const compareMode = t2 === undefined ? false : t2;
  const secondaryCoverage = t3 === undefined ? null : t3;
  const eligibilityStatus = t4 === undefined ? null : t4;
  const variant = t5 === undefined ? "outline" : t5;
  const [isEditing, setIsEditing] = useState(false);
  const [displayCoverage, setDisplayCoverage] = useState(coverage);
  const {
    roles
  } = useUser();
  let t6;
  if ($[0] !== roles) {
    t6 = canWriteSOR(roles);
    $[0] = roles;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  const canWrite = t6;
  const effectiveIsEditable = isEditable && canWrite;
  let t7;
  let t8;
  if ($[2] !== coverage) {
    t7 = () => {
      setDisplayCoverage(coverage);
    };
    t8 = [coverage];
    $[2] = coverage;
    $[3] = t7;
    $[4] = t8;
  } else {
    t7 = $[3];
    t8 = $[4];
  }
  useEffect(t7, t8);
  if (!displayCoverage) {
    return null;
  }
  if (isEditing) {
    let t9;
    if ($[5] !== onCoverageSaved) {
      t9 = savedCoverage => {
        if (savedCoverage) {
          setDisplayCoverage(savedCoverage);
          onCoverageSaved?.(savedCoverage);
        }
        setIsEditing(false);
      };
      $[5] = onCoverageSaved;
      $[6] = t9;
    } else {
      t9 = $[6];
    }
    let t10;
    if ($[7] !== displayCoverage || $[8] !== t9 || $[9] !== type) {
      t10 = <CoverageCardEditable coverage={displayCoverage} type={type} onClose={t9} />;
      $[7] = displayCoverage;
      $[8] = t9;
      $[9] = type;
      $[10] = t10;
    } else {
      t10 = $[10];
    }
    return t10;
  }
  let T0;
  let T1;
  let T2;
  let cardActionsProps;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t9;
  if ($[11] !== className || $[12] !== compareMode || $[13] !== displayCoverage || $[14] !== eligibilityStatus || $[15] !== headerActions || $[16] !== secondaryCoverage || $[17] !== type || $[18] !== variant) {
    let hasAnyConflicts = false;
    if (compareMode && secondaryCoverage) {
      hasAnyConflicts = hasConflicts(COVERAGE_FIELDS, displayCoverage, secondaryCoverage);
    }
    let t21;
    if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
      t21 = () => setIsEditing(true);
      $[35] = t21;
    } else {
      t21 = $[35];
    }
    let t22;
    if ($[36] !== displayCoverage.id) {
      t22 = createCardActionsProps({
        id: displayCoverage.id,
        label: "Coverage ID",
        customProps: [{
          label: "Edit Coverage",
          onClick: t21
        }]
      });
      $[36] = displayCoverage.id;
      $[37] = t22;
    } else {
      t22 = $[37];
    }
    cardActionsProps = t22;
    T2 = ContextMenu;
    T1 = ContextMenuTrigger;
    t20 = true;
    t18 = "w-full outline-none";
    t19 = 0;
    T0 = ValerieCard;
    t13 = `${type} Coverage`;
    if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <ShieldCheck />;
      $[38] = t14;
    } else {
      t14 = $[38];
    }
    if ($[39] !== className) {
      t15 = cn("w-full overflow-hidden", className);
      $[39] = className;
      $[40] = t15;
    } else {
      t15 = $[40];
    }
    t16 = variant;
    t17 = "space-y-4";
    t9 = headerActions;
    if ($[41] !== eligibilityStatus) {
      t10 = eligibilityStatus === "active" && <span className="rounded-full bg-success/20 px-2 py-0.5 text-xs font-medium text-success">Eligible</span>;
      $[41] = eligibilityStatus;
      $[42] = t10;
    } else {
      t10 = $[42];
    }
    if ($[43] !== eligibilityStatus) {
      t11 = eligibilityStatus === "inactive" && <span className="rounded-full bg-destructive/20 px-2 py-0.5 text-xs font-medium text-destructive">Ineligible</span>;
      $[43] = eligibilityStatus;
      $[44] = t11;
    } else {
      t11 = $[44];
    }
    t12 = renderBadge(compareMode, secondaryCoverage, hasAnyConflicts);
    $[11] = className;
    $[12] = compareMode;
    $[13] = displayCoverage;
    $[14] = eligibilityStatus;
    $[15] = headerActions;
    $[16] = secondaryCoverage;
    $[17] = type;
    $[18] = variant;
    $[19] = T0;
    $[20] = T1;
    $[21] = T2;
    $[22] = cardActionsProps;
    $[23] = t10;
    $[24] = t11;
    $[25] = t12;
    $[26] = t13;
    $[27] = t14;
    $[28] = t15;
    $[29] = t16;
    $[30] = t17;
    $[31] = t18;
    $[32] = t19;
    $[33] = t20;
    $[34] = t9;
  } else {
    T0 = $[19];
    T1 = $[20];
    T2 = $[21];
    cardActionsProps = $[22];
    t10 = $[23];
    t11 = $[24];
    t12 = $[25];
    t13 = $[26];
    t14 = $[27];
    t15 = $[28];
    t16 = $[29];
    t17 = $[30];
    t18 = $[31];
    t19 = $[32];
    t20 = $[33];
    t9 = $[34];
  }
  let t21;
  if ($[45] !== cardActionsProps || $[46] !== effectiveIsEditable) {
    t21 = effectiveIsEditable && <EntityDropdownMenu cardActionsProps={cardActionsProps} />;
    $[45] = cardActionsProps;
    $[46] = effectiveIsEditable;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== t10 || $[49] !== t11 || $[50] !== t12 || $[51] !== t21 || $[52] !== t9) {
    t22 = <>{t9}{t10}{t11}{t12}{t21}</>;
    $[48] = t10;
    $[49] = t11;
    $[50] = t12;
    $[51] = t21;
    $[52] = t9;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  let t23;
  if ($[54] !== compareMode || $[55] !== displayCoverage || $[56] !== secondaryCoverage) {
    t23 = displayCoverage.coverageType === CoverageType.SELF_PAY ? <div className="text-sm"><span className="font-semibold">Coverage Type:</span> Self Pay</div> : <FieldLayout fields={COVERAGE_FIELDS} patient={displayCoverage} secondaryObject={secondaryCoverage} compareMode={compareMode} />;
    $[54] = compareMode;
    $[55] = displayCoverage;
    $[56] = secondaryCoverage;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  let t24;
  if ($[58] !== T0 || $[59] !== t13 || $[60] !== t14 || $[61] !== t15 || $[62] !== t16 || $[63] !== t17 || $[64] !== t22 || $[65] !== t23) {
    t24 = <T0 title={t13} icon={t14} className={t15} variant={t16} contentClassName={t17} actions={t22}>{t23}</T0>;
    $[58] = T0;
    $[59] = t13;
    $[60] = t14;
    $[61] = t15;
    $[62] = t16;
    $[63] = t17;
    $[64] = t22;
    $[65] = t23;
    $[66] = t24;
  } else {
    t24 = $[66];
  }
  let t25;
  if ($[67] !== t18 || $[68] !== t19 || $[69] !== t24) {
    t25 = <div className={t18} tabIndex={t19}>{t24}</div>;
    $[67] = t18;
    $[68] = t19;
    $[69] = t24;
    $[70] = t25;
  } else {
    t25 = $[70];
  }
  let t26;
  if ($[71] !== T1 || $[72] !== t20 || $[73] !== t25) {
    t26 = <T1 asChild={t20}>{t25}</T1>;
    $[71] = T1;
    $[72] = t20;
    $[73] = t25;
    $[74] = t26;
  } else {
    t26 = $[74];
  }
  let t27;
  if ($[75] !== cardActionsProps || $[76] !== effectiveIsEditable) {
    t27 = effectiveIsEditable && <CardActions {...cardActionsProps} isContextMenu={true} />;
    $[75] = cardActionsProps;
    $[76] = effectiveIsEditable;
    $[77] = t27;
  } else {
    t27 = $[77];
  }
  let t28;
  if ($[78] !== T2 || $[79] !== t26 || $[80] !== t27) {
    t28 = <T2>{t26}{t27}</T2>;
    $[78] = T2;
    $[79] = t26;
    $[80] = t27;
    $[81] = t28;
  } else {
    t28 = $[81];
  }
  return t28;
}